import InfiniteSelect from 'components/InfiniteSelect';
import {
  BaseFilter,
  BaseFilterConfigParams,
} from 'components/SearchAndFilters/FilterTypes';
import SelectedFilterBadge from 'components/SearchAndFilters/SelectedFiltersBar/SelectedFilterBadge';
import { useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
// import { fetchHosts } from 'lib/helper';
import React from 'react';
import { companyIdAtom } from 'views/state';

import { policyIdFilterAtom } from './state';

export const getAssetsFetchConfig = (
  company_id: number,
  policyId?: number,
  hostsIn?: number[],
) => {
  let baseUrl = '';
  const additionalParams = {};

  // currently lets only consider cloud agents with pcm module enabled
  baseUrl = '/pcm/filter-hosts/';
  additionalParams['company_id'] = company_id;
  // if policyId is provided, add it to the additional params
  if (policyId) {
    additionalParams['policy_id'] = policyId;
  }

  // if the current selected values is a number array
  // it means that we need to add an additional filter for fetch
  if (hostsIn?.length) {
    additionalParams['hosts_in'] = hostsIn;
  }

  return { baseUrl, additionalParams };
};

/**
 * Configuration for a custom filter.
 * Allows for custom filter rendering logic.
 */
export class HostsFilterConfig extends BaseFilter {
  badgeLabel: string;
  hostsData: any;

  constructor(
    params: BaseFilterConfigParams & {
      hostsData: any;
      badgeLabel: string;
    },
  ) {
    super({ ...params });
    this.badgeLabel = params.badgeLabel;
    this.hostsData = params.hostsData;
  }

  render(onSelectedValueChange: () => void) {
    return (
      <HostsFilter
        key={this.id}
        config={this}
        onSelectedValueChange={onSelectedValueChange}
      />
    );
  }

  renderFilterBadge(onSelectedValueChange: () => void): JSX.Element | null {
    const selectedHosts = this.getSelectedValues().map((host) => {
      // for sast hosts, display name/branch
      return host.alias ? `${host.alias} (${host.name})` : host.name;
    });
    return (
      <SelectedFilterBadge
        label={this.badgeLabel}
        values={selectedHosts}
        onRemove={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
      />
    );
  }
}

export const fetchHosts = async ({
  baseUrl,
  search,
  limit,
  page,
  additionalParams = {},
}) => {
  if (!search) search = '';
  if (page === undefined) page = 0;
  if (!baseUrl) return null;

  let url = `${baseUrl}?&limit=${limit}&search=${search}&offset=${
    page * limit
  }`;

  Object.entries(additionalParams).forEach(([key, value]) => {
    url += `&${key}=${value}`;
  });

  const response = await callApi(url, 'GET', {});
  if (response.status === 200 || response.status === 201) {
    return {
      results: response.data.results,
      hasMore: response.data.count > (page + 1) * limit,
    };
  }

  return {
    results: [],
    hasMore: false,
  };
};

interface HostsFilterProps {
  config: HostsFilterConfig;
  onSelectedValueChange: () => void; // Callback for value changes
}

const HOSTS_LIMIT = 100;

const HostsFilter: React.FC<HostsFilterProps> = ({
  config,
  onSelectedValueChange,
}) => {
  const company_id = useAtomValue(companyIdAtom);
  const policyId = useAtomValue(policyIdFilterAtom);

  const { baseUrl, additionalParams } = getAssetsFetchConfig(
    company_id,
    policyId && policyId !== 'all' ? policyId : undefined,
  );
  // Key is required for cache, if key gets changed, option will reload
  // Added company id with key to update the host options when company changes
  let key = company_id;

  return (
    <>
      <InfiniteSelect
        fetcherFn={(search, page) =>
          fetchHosts({
            baseUrl: baseUrl,
            search,
            page,
            limit: HOSTS_LIMIT,
            additionalParams: additionalParams,
          }).then((result) => {
            // if the current selected values is a number array
            // then we need to make appropriate selection
            if (
              config.currentSelectedValues?.every(
                (value) => typeof value === 'number',
              )
            ) {
              const selectedHosts = result?.results.filter((host) =>
                config.currentSelectedValues.includes(host.id),
              );
              config.currentSelectedValues = selectedHosts;
              onSelectedValueChange?.();
            }
            return result || { results: [], hasMore: false };
          })
        }
        cacheKey={key}
        onChange={(selectedOptions) => {
          // eslint-disable-next-line no-console
          // console.log('selectedOptions:', selectedOptions);
          config.currentSelectedValues = selectedOptions;
          onSelectedValueChange?.();
        }}
        value={config.currentSelectedValues}
        // isDisabled={sourceType === ''}
        defaultOptions={config.hostsData}
        placeholder="Search..."
        selectedLabel="Selected"
        optionsLabel="Host Assets"
        getOptionLabel={(host) =>
          host.alias ? `${host.alias} (${host.name})` : host.name
        }
        getOptionValue={(host) => host.host_id}
      />
    </>
  );
};

export default HostsFilter;
